import { call, take, put, retry, race, takeLatest } from 'redux-saga/effects';
import * as apollo from 'apollo/projects.query';
import { pushSiteError } from 'containers/App/actions';
import { PUSH_AUTH_ERROR } from 'containers/App/constants';
import * as types from './types';

const MAX_RETRIES = 5;
const RETRY_DELAY = 5000;

function* getProjectLimits() {
  try {
    const { response, cancel } = yield race({
      response: retry(MAX_RETRIES, RETRY_DELAY, apollo.weddingsLimit),
      cancel: take(PUSH_AUTH_ERROR),
    });
    if (cancel) return;
    yield put({ type: types.GET_PROJECT_LIMITS_SUCCESS, payload: response.weddingsLimit });
  } catch (err) {
    yield put(pushSiteError({ message: 'Please try again later or contact support' }));
  }
}

// First project load for active and archived projects.
function* getInitialProjects(action) {
  const { payload } = action || {};
  const archivedPayload = {
    ...payload,
    isArchived: !payload.isArchived,
    sortKey: payload.sortArchiveKey || payload.sortKey,
  };

  try {
    const { response: activeResponse, cancel } = yield race({
      response: retry(MAX_RETRIES, RETRY_DELAY, apollo.fetchUserProjects, payload),
      cancel: take(PUSH_AUTH_ERROR),
    });
    if (cancel) return;
    yield put({ type: types.GET_PROJECTS_SUCCESS, payload: activeResponse, projectStatus: 'active' });
  } catch (err) {
    yield put(pushSiteError({ message: 'Please try again later or contact support' }));
  }

  try {
    const { response: archivedResponse } = yield race({
      response: retry(MAX_RETRIES, RETRY_DELAY, apollo.fetchUserProjects, archivedPayload),
      cancel: take(PUSH_AUTH_ERROR),
    });
    yield put({ type: types.GET_PROJECTS_SUCCESS, payload: archivedResponse, projectStatus: 'archived' });
  } catch (err) {
    // archived projects failing should not block the page
  }

  yield put({ type: types.SET_PROJECTS_LOADED });
}

function* getProjects(action) {
  const { payload } = action || {};
  const { page } = payload || 0;
  const projectStatus = payload.key !== '' ? 'searched' : payload.isArchived ? 'archived' : 'active';

  try {
    const { response, cancel } = yield race({
      response: retry(MAX_RETRIES, RETRY_DELAY, apollo.fetchUserProjects, payload),
      cancel: take(PUSH_AUTH_ERROR),
    });
    if (cancel) return;
    if (page === 0) {
      yield put({ type: types.GET_PROJECTS_SUCCESS, payload: response, projectStatus });
    } else {
      yield put({ type: types.GET_MORE_PROJECTS_SUCCESS, payload: response, projectStatus });
    }
  } catch (err) {
    yield put(pushSiteError({ message: 'Please try again later or contact support' }));
  }
};

function* archiveProject(action) {
  const { payload } = action || {};
  const { action: projectAction } = payload || {};
  const isArchiving = projectAction === 'archive' ? true : projectAction === 'unarchive' ? false : null;
  if (isArchiving !== null) {
    try {
      const response = yield call(apollo.archiveWedding, payload);
      // Ensure that the right operation applied.
      if (response.archiveWedding?.wedding?.isArchived !== isArchiving) {
        yield put({ type: types.UPDATE_PROJECT_FAIL });
      }
      yield put({ type: types.ARCHIVE_PROJECT_SUCCESS, payload: response });
      yield put({ type: types.GET_PROJECT_LIMITS });
    } catch (err) {
      yield put({ type: types.UPDATE_PROJECT_FAIL });
    }
  }
  else {
    yield put({ type: types.UPDATE_PROJECT_FAIL });
  }
};

function* deleteProject(action) {
  const { payload } = action || {};
  try {
    const response = yield call(apollo.deleteWedding, payload);
    const { isArchived, isDeleted } = response.deleteWedding.wedding;
    if (!isArchived || !isDeleted) {
      yield put({ type: types.UPDATE_PROJECT_FAIL });
    }
    yield put({ type: types.DELETE_PROJECT_SUCCESS, payload: response });
  } catch (err) {
    yield put({ type: types.UPDATE_PROJECT_FAIL });
  }
};

function* getProjectFeatures(action) {
  const { payload } = action || {};
  try {
    const response = yield call(apollo.fetchProjectFeatures, payload);
    yield put({ type: types.GET_PROJECT_FEATURES_SUCCESS, payload: response?.projectFeatures });
  } catch (err) {
    yield put({ type: types.GET_PROJECT_FEATURES_FAIL });
  }
}

function* updateProjectFeatures(action) {
  const { payload } = action || {};
  try {
    const response = yield call(apollo.updateProjectFeatures, payload);
    yield put({ type: types.UPDATE_PROJECT_FEATURES_SUCCESS, payload: response?.updateProjectFeatures });
  } catch (err) {
    yield put({ type: types.UPDATE_PROJECT_FEATURES_FAIL });
  }
}

function* weddingPermissions(action){
  const { payload } = action || {};
  try {
    const response = yield call(apollo.weddingPermissions, payload);
    yield put({ type: types.WEDDING_PERMISSIONS_SUCCESS, payload: response?.weddingsPermissions });
  } catch (err) {
    yield put({ type: types.WEDDING_PERMISSIONS_FAIL });
  }
}

export default function* watchList() {
  yield takeLatest(types.GET_PROJECT_LIMITS, getProjectLimits);
  yield takeLatest(types.GET_INITIAL_PROJECTS, getInitialProjects);
  yield takeLatest(types.GET_PROJECTS, getProjects);
  yield takeLatest(types.ARCHIVE_PROJECT, archiveProject);
  yield takeLatest(types.DELETE_PROJECT, deleteProject);
  yield takeLatest(types.GET_PROJECT_FEATURES, getProjectFeatures);
  yield takeLatest(types.UPDATE_PROJECT_FEATURES, updateProjectFeatures);
  yield takeLatest(types.WEDDING_PERMISSIONS, weddingPermissions);
}
